import type { MouseEvent } from "react"
import { useCallback } from "react"
import { useNavigate } from "@tanstack/react-router"

type PressEvent = Pick<
	MouseEvent,
	"button" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey"
>

/** A left-button press with no modifier. Cmd/Ctrl/Shift-click and the middle button keep the browser's new-tab handling. */
export function isPlainPress(event: PressEvent): boolean {
	return (
		event.button === 0 &&
		!event.metaKey &&
		!event.ctrlKey &&
		!event.shiftKey &&
		!event.altKey
	)
}

/**
 * Nav rows act on mousedown, like Linear's, instead of waiting for mouseup.
 * The click that follows is ignored; a keyboard click (detail 0) still acts.
 * The press never focuses the row: Chrome turns a mouse-focused row's ring on at the next key press.
 */
export function pressToActivate(activate: () => void) {
	return {
		onMouseDown: (event: MouseEvent) => {
			if (!isPlainPress(event)) return
			event.preventDefault()
			activate()
		},
		onClick: (event: MouseEvent) => {
			if (event.detail === 0) activate()
		},
	}
}

/** The link version: navigate on mousedown and swallow the click, so the route changes once. */
export function useLinkPress(href: string, onNavigate?: () => void) {
	const navigate = useNavigate()
	const onMouseDown = useCallback(
		(event: MouseEvent) => {
			if (!isPlainPress(event)) return
			event.preventDefault()
			onNavigate?.()
			void navigate({ href: href })
		},
		[href, navigate, onNavigate],
	)
	const onClick = useCallback(
		(event: MouseEvent) => {
			if (event.detail > 0 && isPlainPress(event)) {
				event.preventDefault()
				return
			}
			onNavigate?.()
		},
		[onNavigate],
	)
	return { onMouseDown, onClick }
}
